import {
	AlertCircle,
	BarChart3,
	ChevronLeft,
	ChevronRight,
	Loader2,
	Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import AdminPageHeader from "@/components/AdminPageHeader";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	getUserDisplayName,
	type TopUser,
	useUsageStats,
	useUserActivity,
} from "@/hooks/useUsageStats";
import { cn } from "@/lib/utils";
import { inputClassName } from "@/pages/RewardsAdminPage";

const DATE_RANGE_PRESETS = [
	{ label: "7d", days: 7 },
	{ label: "14d", days: 14 },
	{ label: "30d", days: 30 },
	{ label: "90d", days: 90 },
] as const;

function formatChartDate(dateStr: string): string {
	const d = new Date(dateStr);
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// --- Usage Monitoring Section ---

const USERS_PER_PAGE = 10;

export default function UsageMonitoringSection({
	eventNames,
}: {
	eventNames: string[];
}) {
	const [days, setDays] = useState(30);
	const [eventFilter, setEventFilter] = useState<string>("");
	const [usersPage, setUsersPage] = useState(0);
	const [selectedUser, setSelectedUser] = useState<TopUser | null>(null);

	const handleDaysChange = (d: number) => {
		setDays(d);
		setUsersPage(0);
	};

	const handleEventFilterChange = (v: string) => {
		setEventFilter(v);
		setUsersPage(0);
	};

	const params = useMemo(() => {
		const now = new Date();
		const start = new Date(now);
		start.setDate(start.getDate() - days);
		return {
			startDate: start.toISOString(),
			endDate: now.toISOString(),
			eventName: eventFilter || undefined,
			topN: 100,
		};
	}, [days, eventFilter]);

	const { data, isLoading, error } = useUsageStats(params);

	const totalEvents = useMemo(
		() => (data?.dailyCounts ?? []).reduce((sum, d) => sum + d.count, 0),
		[data?.dailyCounts],
	);

	const avgPerDay = useMemo(() => {
		const counts = data?.dailyCounts ?? [];
		if (counts.length === 0) return 0;
		return Math.round(totalEvents / counts.length);
	}, [data?.dailyCounts, totalEvents]);

	const peakDay = useMemo(() => {
		const counts = data?.dailyCounts ?? [];
		if (counts.length === 0) return null;
		return counts.reduce(
			(max, d) => (d.count > max.count ? d : max),
			counts[0],
		);
	}, [data?.dailyCounts]);

	const totalUsers = data?.topUsers.length ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalUsers / USERS_PER_PAGE));
	const pagedUsers = useMemo(
		() =>
			(data?.topUsers ?? []).slice(
				usersPage * USERS_PER_PAGE,
				(usersPage + 1) * USERS_PER_PAGE,
			),
		[data?.topUsers, usersPage],
	);

	return (
		<section className="space-y-6">
			<AdminPageHeader
				title="Usage Monitoring"
				description="Event activity and top users over time."
				actions={
					<>
						<select
							value={eventFilter}
							onChange={(e) => handleEventFilterChange(e.target.value)}
							className={cn(
								inputClassName,
								"w-auto min-w-[160px] text-xs py-2",
							)}
						>
							<option value="">All events</option>
							{eventNames.map((name) => (
								<option key={name} value={name}>
									{name}
								</option>
							))}
						</select>

						<div className="flex rounded-lg border border-divider overflow-hidden">
							{DATE_RANGE_PRESETS.map((preset) => (
								<button
									key={preset.days}
									type="button"
									onClick={() => handleDaysChange(preset.days)}
									className={cn(
										"px-3 py-1.5 text-xs font-medium transition-colors",
										days === preset.days
											? "bg-brand text-white"
											: "bg-bg-secondary text-text-secondary hover:bg-bg-tertiary",
									)}
								>
									{preset.label}
								</button>
							))}
						</div>
					</>
				}
			/>

			{isLoading && (
				<div className="flex items-center justify-center py-16">
					<Loader2 className="w-6 h-6 animate-spin text-brand" />
				</div>
			)}

			{error && (
				<div className="flex items-center gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/10">
					<AlertCircle className="w-5 h-5 text-destructive shrink-0" />
					<p className="text-sm text-destructive">{error.message}</p>
				</div>
			)}

			{data && !isLoading && (
				<>
					{/* Summary Cards */}
					<div className="grid grid-cols-3 gap-4">
						<div className="rounded-xl border border-divider bg-bg-secondary/50 p-4 space-y-1">
							<p className="text-xs text-text-tertiary font-medium uppercase tracking-wider">
								Total Events
							</p>
							<p className="text-2xl font-semibold text-text-default tabular-nums">
								{totalEvents.toLocaleString()}
							</p>
						</div>
						<div className="rounded-xl border border-divider bg-bg-secondary/50 p-4 space-y-1">
							<p className="text-xs text-text-tertiary font-medium uppercase tracking-wider">
								Avg / Day
							</p>
							<p className="text-2xl font-semibold text-text-default tabular-nums">
								{avgPerDay.toLocaleString()}
							</p>
						</div>
						<div className="rounded-xl border border-divider bg-bg-secondary/50 p-4 space-y-1">
							<p className="text-xs text-text-tertiary font-medium uppercase tracking-wider">
								Peak Day
							</p>
							<p className="text-2xl font-semibold text-text-default tabular-nums">
								{peakDay ? peakDay.count.toLocaleString() : "—"}
							</p>
							{peakDay && (
								<p className="text-[10px] text-text-tertiary">
									{formatChartDate(peakDay.date)}
								</p>
							)}
						</div>
					</div>

					{/* Chart */}
					<div className="rounded-xl border border-divider bg-bg-secondary/30 p-4">
						<h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
							<BarChart3 className="w-4 h-4" />
							Daily Events
						</h3>
						{data.dailyCounts.length === 0 ? (
							<div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
								No events in this period
							</div>
						) : (
							<ResponsiveContainer width="100%" height={280}>
								<AreaChart
									data={data.dailyCounts}
									margin={{ top: 4, right: 4, bottom: 0, left: -12 }}
								>
									<defs>
										<linearGradient
											id="eventGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="5%"
												stopColor="hsl(var(--brand))"
												stopOpacity={0.3}
											/>
											<stop
												offset="95%"
												stopColor="hsl(var(--brand))"
												stopOpacity={0}
											/>
										</linearGradient>
									</defs>
									<CartesianGrid
										strokeDasharray="3 3"
										stroke="hsl(var(--divider))"
										vertical={false}
									/>
									<XAxis
										dataKey="date"
										tickFormatter={formatChartDate}
										tick={{ fontSize: 11, fill: "hsl(var(--text-tertiary))" }}
										axisLine={false}
										tickLine={false}
										interval="preserveStartEnd"
									/>
									<YAxis
										tick={{ fontSize: 11, fill: "hsl(var(--text-tertiary))" }}
										axisLine={false}
										tickLine={false}
										allowDecimals={false}
									/>
									<Tooltip
										contentStyle={{
											backgroundColor: "hsl(var(--bg-secondary))",
											border: "1px solid hsl(var(--divider))",
											borderRadius: "8px",
											fontSize: "12px",
											color: "hsl(var(--text-default))",
										}}
										labelFormatter={(label) => formatChartDate(String(label))}
										formatter={(value) => [
											Number(value).toLocaleString(),
											"Events",
										]}
									/>
									<Area
										type="monotone"
										dataKey="count"
										stroke="hsl(var(--brand))"
										strokeWidth={2}
										fill="url(#eventGradient)"
									/>
								</AreaChart>
							</ResponsiveContainer>
						)}
					</div>

					{/* Top Users Table */}
					<div className="rounded-xl border border-divider bg-bg-secondary/30 overflow-hidden">
						<div className="px-4 py-3 border-b border-divider flex items-center gap-2">
							<Users className="w-4 h-4 text-text-tertiary" />
							<h3 className="text-sm font-medium text-text-secondary">
								Most Active Users
							</h3>
							<span className="text-xs text-text-tertiary ml-auto">
								{totalUsers} user{totalUsers !== 1 ? "s" : ""} &middot; Last{" "}
								{days} days
							</span>
						</div>
						{data.topUsers.length === 0 ? (
							<div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
								No user activity in this period
							</div>
						) : (
							<>
								<div className="divide-y divide-divider">
									{pagedUsers.map((u, i) => {
										const rank = usersPage * USERS_PER_PAGE + i;
										const display = getUserDisplayName(u);
										return (
											<button
												key={u.userId}
												type="button"
												onClick={() => setSelectedUser(u)}
												className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-bg-tertiary/30 transition-colors text-left"
											>
												<span
													className={cn(
														"w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
														rank === 0
															? "bg-yellow-400/20 text-yellow-400"
															: rank === 1
																? "bg-gray-300/20 text-gray-400"
																: rank === 2
																	? "bg-amber-600/20 text-amber-600"
																	: "bg-bg-tertiary text-text-tertiary",
													)}
												>
													{rank + 1}
												</span>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-1.5">
														<UserAccountIcon type={display.type} />
														<p className="text-sm text-text-default truncate">
															{display.name}
														</p>
													</div>
													<p className="text-[10px] text-text-tertiary font-mono truncate">
														{u.userId.slice(0, 8)}...
													</p>
												</div>
												{/* Bar indicator */}
												<div className="w-24 hidden sm:block">
													<div className="h-1.5 rounded-full bg-bg-tertiary overflow-hidden">
														<div
															className="h-full rounded-full bg-brand transition-all"
															style={{
																width: `${Math.max(4, (u.eventCount / (data.topUsers.at(0)?.eventCount ?? 1)) * 100)}%`,
															}}
														/>
													</div>
												</div>
												<span className="text-sm font-medium text-text-default tabular-nums shrink-0">
													{u.eventCount.toLocaleString()}
												</span>
											</button>
										);
									})}
								</div>

								{/* Pagination */}
								{totalPages > 1 && (
									<div className="flex items-center justify-between px-4 py-2.5 border-t border-divider bg-bg-tertiary/20">
										<span className="text-xs text-text-tertiary">
											Page {usersPage + 1} of {totalPages}
										</span>
										<div className="flex items-center gap-1">
											<button
												type="button"
												disabled={usersPage === 0}
												onClick={() => setUsersPage((p) => Math.max(0, p - 1))}
												className={cn(
													"p-1 rounded-md transition-colors",
													usersPage === 0
														? "text-text-tertiary/40 cursor-not-allowed"
														: "text-text-secondary hover:bg-bg-tertiary hover:text-text-default",
												)}
											>
												<ChevronLeft className="w-4 h-4" />
											</button>
											<button
												type="button"
												disabled={usersPage >= totalPages - 1}
												onClick={() =>
													setUsersPage((p) => Math.min(totalPages - 1, p + 1))
												}
												className={cn(
													"p-1 rounded-md transition-colors",
													usersPage >= totalPages - 1
														? "text-text-tertiary/40 cursor-not-allowed"
														: "text-text-secondary hover:bg-bg-tertiary hover:text-text-default",
												)}
											>
												<ChevronRight className="w-4 h-4" />
											</button>
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</>
			)}

			{/* User Profile & Activity Modal */}
			<UserActivityModal
				user={selectedUser}
				open={!!selectedUser}
				onClose={() => setSelectedUser(null)}
				startDate={params.startDate}
				endDate={params.endDate}
				days={days}
			/>
		</section>
	);
}

// --- Account type icon ---

const ACCOUNT_TYPE_COLORS: Record<string, string> = {
	discord: "text-[#5865F2]",
	twitter: "text-[#1DA1F2]",
	github: "text-text-default",
	google: "text-[#EA4335]",
	email: "text-text-tertiary",
	privy: "text-text-tertiary",
};

function UserAccountIcon({ type }: { type: string }) {
	const colorClass = ACCOUNT_TYPE_COLORS[type] ?? "text-text-tertiary";

	switch (type) {
		case "discord":
			return (
				<svg
					viewBox="0 0 24 24"
					className={cn("w-3.5 h-3.5 shrink-0", colorClass)}
					fill="currentColor"
					aria-hidden="true"
				>
					<title>Discord</title>
					<path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" />
				</svg>
			);
		case "twitter":
			return (
				<svg
					viewBox="0 0 24 24"
					className={cn("w-3.5 h-3.5 shrink-0", colorClass)}
					fill="currentColor"
					aria-hidden="true"
				>
					<title>Twitter / X</title>
					<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
				</svg>
			);
		case "github":
			return (
				<svg
					viewBox="0 0 24 24"
					className={cn("w-3.5 h-3.5 shrink-0", colorClass)}
					fill="currentColor"
					aria-hidden="true"
				>
					<title>GitHub</title>
					<path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
				</svg>
			);
		default:
			return null;
	}
}

// --- User Activity Modal ---

function UserActivityModal({
	user,
	open,
	onClose,
	startDate,
	endDate,
	days,
}: {
	user: TopUser | null;
	open: boolean;
	onClose: () => void;
	startDate?: string;
	endDate?: string;
	days: number;
}) {
	const { data, isLoading } = useUserActivity({
		userId: user?.userId ?? null,
		startDate,
		endDate,
		enabled: open && !!user,
	});

	if (!user) return null;

	const display = getUserDisplayName(user);
	const accounts = user.linkedAccounts?.accounts ?? [];
	const totalActivity = (data?.dailyActivity ?? []).reduce(
		(s, d) => s + d.count,
		0,
	);
	const maxBreakdown = data?.eventBreakdown.at(0)?.count ?? 1;

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<UserAccountIcon type={display.type} />
						{display.name}
					</DialogTitle>
					<DialogDescription>
						User activity over the last {days} days
					</DialogDescription>
				</DialogHeader>

				{/* Profile Info */}
				<div className="space-y-4">
					{/* Linked Accounts */}
					<div className="space-y-2">
						<p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
							Linked Accounts
						</p>
						<div className="flex flex-wrap gap-2">
							{accounts.length === 0 && (
								<span className="text-xs text-text-tertiary">
									No linked accounts
								</span>
							)}
							{accounts.map((account) => (
								<LinkedAccountBadge
									key={`${account.type}-${"address" in account ? account.address : "username" in account ? account.username : "subject" in account ? account.subject : ""}`}
									account={account}
								/>
							))}
						</div>
					</div>

					{/* IDs */}
					<div className="grid grid-cols-2 gap-3">
						<div className="space-y-0.5">
							<p className="text-[10px] text-text-tertiary uppercase tracking-wider">
								User ID
							</p>
							<p className="text-xs text-text-secondary font-mono break-all">
								{user.userId}
							</p>
						</div>
						{user.privyId && (
							<div className="space-y-0.5">
								<p className="text-[10px] text-text-tertiary uppercase tracking-wider">
									Privy ID
								</p>
								<p className="text-xs text-text-secondary font-mono break-all">
									{user.privyId}
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Activity */}
				{isLoading && (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="w-5 h-5 animate-spin text-brand" />
					</div>
				)}

				{data && !isLoading && (
					<div className="space-y-4 pt-2">
						{/* Mini activity chart */}
						{data.dailyActivity.length > 0 && (
							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
										Daily Activity
									</p>
									<p className="text-xs text-text-tertiary">
										{totalActivity.toLocaleString()} total events
									</p>
								</div>
								<ResponsiveContainer width="100%" height={140}>
									<AreaChart
										data={data.dailyActivity}
										margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
									>
										<defs>
											<linearGradient
												id="userActivityGradient"
												x1="0"
												y1="0"
												x2="0"
												y2="1"
											>
												<stop
													offset="5%"
													stopColor="hsl(var(--brand))"
													stopOpacity={0.3}
												/>
												<stop
													offset="95%"
													stopColor="hsl(var(--brand))"
													stopOpacity={0}
												/>
											</linearGradient>
										</defs>
										<CartesianGrid
											strokeDasharray="3 3"
											stroke="hsl(var(--divider))"
											vertical={false}
										/>
										<XAxis
											dataKey="date"
											tickFormatter={(d) => formatChartDate(String(d))}
											tick={{
												fontSize: 10,
												fill: "hsl(var(--text-tertiary))",
											}}
											axisLine={false}
											tickLine={false}
											interval="preserveStartEnd"
										/>
										<YAxis
											tick={{
												fontSize: 10,
												fill: "hsl(var(--text-tertiary))",
											}}
											axisLine={false}
											tickLine={false}
											allowDecimals={false}
										/>
										<Tooltip
											contentStyle={{
												backgroundColor: "hsl(var(--bg-secondary))",
												border: "1px solid hsl(var(--divider))",
												borderRadius: "8px",
												fontSize: "11px",
												color: "hsl(var(--text-default))",
											}}
											labelFormatter={(label) => formatChartDate(String(label))}
											formatter={(value) => [
												Number(value).toLocaleString(),
												"Events",
											]}
										/>
										<Area
											type="monotone"
											dataKey="count"
											stroke="hsl(var(--brand))"
											strokeWidth={1.5}
											fill="url(#userActivityGradient)"
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>
						)}

						{/* Event Breakdown */}
						{data.eventBreakdown.length > 0 && (
							<div className="space-y-2">
								<p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
									Event Breakdown
								</p>
								<div className="space-y-1.5">
									{data.eventBreakdown.map((e) => (
										<div key={e.eventName} className="flex items-center gap-3">
											<span className="text-xs text-text-secondary truncate min-w-0 flex-1">
												{e.eventName}
											</span>
											<div className="w-28 shrink-0">
												<div className="h-1.5 rounded-full bg-bg-tertiary overflow-hidden">
													<div
														className="h-full rounded-full bg-brand/70 transition-all"
														style={{
															width: `${Math.max(4, (e.count / maxBreakdown) * 100)}%`,
														}}
													/>
												</div>
											</div>
											<span className="text-xs font-medium text-text-default tabular-nums w-10 text-right shrink-0">
												{e.count.toLocaleString()}
											</span>
										</div>
									))}
								</div>
							</div>
						)}

						{data.eventBreakdown.length === 0 &&
							data.dailyActivity.length === 0 && (
								<div className="text-center py-6 text-sm text-text-tertiary">
									No activity in this period
								</div>
							)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

// --- Linked Account Badge ---

function LinkedAccountBadge({
	account,
}: {
	account: TopUser["linkedAccounts"] extends { accounts: (infer A)[] } | null
		? A
		: never;
}) {
	switch (account.type) {
		case "discord_oauth":
			return (
				<span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#5865F2]/10 text-[#5865F2] text-xs border border-[#5865F2]/20">
					<UserAccountIcon type="discord" />
					{account.username}
				</span>
			);
		case "twitter_oauth":
			return (
				<span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#1DA1F2]/10 text-[#1DA1F2] text-xs border border-[#1DA1F2]/20">
					<UserAccountIcon type="twitter" />@{account.username}
				</span>
			);
		case "github_oauth":
			return (
				<span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-text-default/5 text-text-default text-xs border border-divider">
					<UserAccountIcon type="github" />
					{account.username}
				</span>
			);
		case "google_oauth":
			return (
				<span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#EA4335]/10 text-[#EA4335] text-xs border border-[#EA4335]/20">
					{account.name ?? account.email}
				</span>
			);
		case "email":
			return (
				<span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-bg-tertiary text-text-secondary text-xs border border-divider">
					{account.address}
				</span>
			);
		case "wallet":
			return (
				<span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-bg-tertiary text-text-secondary text-xs border border-divider font-mono">
					{account.address.slice(0, 6)}...{account.address.slice(-4)}
					<span className="text-[10px] text-text-tertiary">
						({account.chainType})
					</span>
				</span>
			);
		default:
			return null;
	}
}
